import { JSEncrypt } from 'jsencrypt';
import { log } from '../JS/Logger';
import { BaseUrl } from '../JS/Common';
class SecureEncrypt {
    constructor() {
        this.encryptor = new JSEncrypt();
        this.publicKey = null;
    }

    async fetchPublicKey() {
        try {
            const response = await fetch(`${BaseUrl}/security/public-key`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            this.publicKey = data.public_key;
            this.encryptor.setPublicKey(this.publicKey);
            return true;
        } catch (error) {
            log.error('Failed to fetch public key:', error);
            throw new Error('Cannot get encryption key from server');
        }
    }

    async encryptText(text) {
        if (!this.publicKey) {
            const success = await this.fetchPublicKey();
            if (!success) {
                throw new Error('Cannot get encryption key');
            }
        }

        try {
            // Create a payload with the text and security metadata
            const payload = {
                data: text,
                _timestamp: Date.now(),
                _nonce: Math.random().toString(36).substring(2, 15)
            };

            // Convert to JSON string
            const jsonString = JSON.stringify(payload);

            // Encrypt with RSA public key
            const encrypted = this.encryptor.encrypt(jsonString);

            if (!encrypted) {
                throw new Error('Encryption failed');
            }

            // Return base64 encoded encrypted string
            return {
                encrypted_data: encrypted,
                timestamp: payload._timestamp
            };
        } catch (error) {
            log.error('Encryption error:', error)
            throw error;
        }
    }

    async encryptData(data) {
        if (!this.publicKey) {
            const success = await this.fetchPublicKey();
            if (!success) {
                throw new Error('Cannot get encryption key');
            }
        }

        try {
            // Add timestamp to prevent replay attacks
            const payload = {
                ...data,
                _timestamp: Date.now(),
                _nonce: Math.random().toString(36).substring(2, 15)
            };

            // Convert to JSON string
            const jsonString = JSON.stringify(payload);

            // Encrypt with RSA public key
            const encrypted = this.encryptor.encrypt(jsonString);

            if (!encrypted) {
                throw new Error('Encryption failed');
            }

            return {
                encrypted_data: encrypted,
                timestamp: payload._timestamp
            };
        } catch (error) {
            log.error('Encryption error:', error)
            throw error;
        }
    }
    async encryptField(value) {
        if (!this.publicKey) {
            const success = await this.fetchPublicKey();
            if (!success) {
                throw new Error('Cannot get encryption key');
            }
        }
        try {
            // For small values, use simple RSA with JSON wrapper
            const payload = {
                data: value.toString(),
                _timestamp: Date.now()
            };
            const encrypted = this.encryptor.encrypt(JSON.stringify(payload));
            if (!encrypted) {
                throw new Error('Encryption failed');
            }
            return {
                encrypted_data: encrypted,
                timestamp: payload._timestamp
            };
        } catch (error) {
            log.error('Field encryption error:', error)
            throw error;
        }
    }
    async encryptJWTToken(token) {
        if (!this.publicKey) {
            const success = await this.fetchPublicKey();
            if (!success) {
                throw new Error('Cannot get encryption key');
            }
        }
        try {
            // 1. Generate AES key (128-bit)
            const aesKey = crypto.getRandomValues(new Uint8Array(16));
            // 2. Convert to hex string (32 chars)
            const aesKeyHex = Array.from(aesKey)
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
            // 3. Encrypt AES key with RSA (JUST the hex string, no JSON!)
            const encryptedKey = this.encryptor.encrypt(aesKeyHex);
            if (!encryptedKey) {
                throw new Error('Failed to encrypt AES key');
            }
            // 4. Encrypt token with AES-GCM
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encodedToken = new TextEncoder().encode(token);
            const cryptoKey = await crypto.subtle.importKey(
                "raw",
                aesKey,
                { name: "AES-GCM" },
                false,
                ["encrypt"]
            );
            const encryptedContent = await crypto.subtle.encrypt(
                {
                    name: "AES-GCM",
                    iv: iv
                },
                cryptoKey,
                encodedToken
            );
            return {
                encrypted_data: {
                    iv: this.arrayBufferToBase64(iv),
                    content: this.arrayBufferToBase64(encryptedContent),
                    encryptedKey: encryptedKey // JSEncrypt returns base64 already
                },
                timestamp: Date.now()
            };
        } catch (error) {
            log.error('JWT encryption error:', error)
            throw error;
        }
    }
    // Helper method
    arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }
}

// Singleton instance
export const secureEncrypt = new SecureEncrypt();